import { getCookie } from 'src/util/cookies';

const experimentTreatments = ['C', 'T1', 'T2', 'T3', 'T4'] as const;

/**
 * Experiment (Weblab) treatment assignments. `C` though `T4` should be more than enough, as we generally only use `C`
 * and `T1` anyway.
 */
export type ExperimentTreatment = typeof experimentTreatments[number];

const experiments = [
  'BRAND_REGISTRY_SPOTLIGHT_906933',
  'BRELUP_HOB_651457',
  'TRANSPARENCY_LINKS_966095',
  'TEST_DO_NOT_REMOVE_123456',
] as const;

export type Experiment = typeof experiments[number];

type WeblabMap = Partial<Record<Experiment, ExperimentTreatment>>;

// Used to cache treatments to prevent repeated DOM and cookie reads
let weblabs: WeblabMap | undefined = undefined;

/**
 * Returns the current treatment assignment for the given experiment (Weblab). This value is pulled from the Weblab
 * meta tag added by Mons via the Weblab Task feature.
 *
 * @see https://w.amazon.com/bin/view/Mons/Weblab/
 */
export function getExperiment(experimentName: Experiment): ExperimentTreatment {
  // Initialize cache and extract Weblabs if they haven't been already
  if (!weblabs) {
    weblabs = document.head.querySelector<HTMLMetaElement>('meta[name="a2z:mons_weblab_treatment"]')
      ?.content
      ?.split(',')
      .reduce(collectWeblabs, {}) ??
        {};

    collectWeblabOverrides();
  }

  return weblabs[experimentName] ?? 'C';
}

/**
 * Clears the Weblab cache for testing purposes.
 */
export function clearWeblabCache() {
  weblabs = undefined;
}

/**
 * If a Weblab's treatment is currently being overridden for integration tests or internal testing, replace that entry
 * in the cache.
 */
function collectWeblabOverrides() {
  getCookie('experiment')
    ?.replace(/['"]/g, '')
    .split('&')
    .forEach((weblabEntry) => {
      const [weblabName, treatment] = weblabEntry.split(':');
      const experiment = weblabName as Experiment;

      if (
        experiments.includes(experiment) && experimentTreatments.includes(treatment as ExperimentTreatment) && weblabs
      ) {
        weblabs[experiment] = treatment as ExperimentTreatment;
      }
    });
}

function collectWeblabs(accumulator: WeblabMap, weblabEntry: string): WeblabMap {
  const [weblabName, treatment] = weblabEntry.split('=');
  const experiment = weblabName as Experiment;

  // If either are null or not a known value, the meta content is malformed so just return the accumulator
  if (experiments.includes(experiment) && experimentTreatments.includes(treatment as ExperimentTreatment)) {
    accumulator[experiment] = treatment as ExperimentTreatment;
  }

  return accumulator;
}
